<template>
  <div class="form-item" :class="{ 'has-error': errorMessage }" :style="{ textAlign: language === 'ar' ? 'right' : 'left', direction: language === 'ar' ? 'rtl' : 'ltr' }" ref="itemEl">
    <div class="form-item-label">
      <label v-if="label" :style="{ fontSize: style.fontSize }">
        {{ label }}
        <span v-if="isRequired" class="required">*</span>
      </label>
      <!-- 标签 -->
      <div class="form-item-tag" :style="{ fontSize: style.fontSize, color: style.tagColor, background: style.tagBackground }" v-if="typeName && showTag">{{ typeName }}</div>
    </div>

    <div class="form-item-content">
      <slot :modelValue="modelValue" :error="!!errorMessage" :updateValue="handleInput"></slot>
      <!-- 错误信息展示 -->
      <div v-if="errorMessage && showValidateMsg" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import Toast from '@/h5/components/toast'
import { ref, computed, inject, onMounted, onUnmounted } from 'vue'
import { cloneDeep, debounce } from 'lodash'

const props = defineProps({
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    default: ''
  },
  // 是否必填，可以单独设置或从rules中获取
  required: {
    type: Boolean,
    default: null
  },
  style: {
    type: Object,
    default: () => ({
      fontSize: '1.1429rem'
    })
  },
  typeName: {
    type: String,
    required: true
  },
  language: {
    type: String,
    required: true
  },
  showValidateMsg: {
    type: Boolean,
    default: true
  },
  showTag: {
    type: Boolean,
    default: true
  }
})

const formContext = inject('formContext')
const itemEl = ref(null)

// 计算是否必填：优先使用props.required，其次从rules中获取
const isRequired = computed(() => {
  // 如果props.required明确设置，则直接返回
  if (props.required !== null) {
    return props.required
  }

  // 从formContext的rules中获取当前字段的规则
  const fieldRules = formContext?.rules?.[props.fieldName]
  if (!fieldRules) return false

  // 检查规则中是否有required属性
  if (Array.isArray(fieldRules)) {
    return fieldRules.some((rule) => rule?.required)
  } else if (typeof fieldRules === 'object') {
    return fieldRules.required
  }

  return false
})

const showToast = debounce(
  (customMessage) => {
    Toast && Toast({ message: customMessage, duration: 1500 })
  },
  500,
  { leading: true, trailing: false }
)

// 注册当前表单项到表单容器
onMounted(() => {
  if (formContext) {
    formContext.registerItem({
      fieldName: props.fieldName,
      $el: itemEl.value
    })
  }
})

// 卸载时移除注册
onUnmounted(() => {
  if (formContext) {
    formContext.unregisterItem({
      fieldName: props.fieldName,
      $el: itemEl.value
    })
  }
})

// 从表单容器获取model值
const modelValue = computed({
  get: () => formContext?.model[props.fieldName],
  set: async (val) => {
    if (formContext) {
      const oldModel = cloneDeep(formContext.model[props.fieldName])
      formContext.model[props.fieldName] = val
      // 触发校验
      const { customMessage, isValid } = await formContext.validateField(props.fieldName)
      // TODO: 如果需要更改值就触发校验，可以在此处加逻辑
      if (!isValid && customMessage) {
        showToast(customMessage)
        formContext.model[props.fieldName] = oldModel
        return
      }
    }
  }
})

// 错误信息
const errorMessage = computed(() => {
  return formContext?.errorFields.value[props.fieldName]
})

// 处理输入事件
const handleInput = (value) => {
  modelValue.value = value
}

// 暴露方法给父组件
defineExpose({
  fieldName: props.fieldName,
  $el: itemEl
})
</script>

<style scoped>
.form-item {
  /* margin-bottom: 1.1429rem; */
}

.form-item-label {
  display: flex;
  align-items: baseline;
  /* margin-bottom: 0.5714rem; */
  font-weight: bold;
  font-size: 1rem;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.form-item-tag {
  font-weight: normal;
  margin: 0 0.7143rem;
  background-color: #c973df;
  padding: 0.2857rem 0.5714rem;
  border-radius: 0.5714rem;
  min-width: fit-content;
}

.required {
  color: #f56c6c;
  position: relative;
  bottom: 0.1429rem;
}

.form-item-content {
  position: relative;
}

.has-error :deep(input),
.has-error :deep(select),
.has-error :deep(textarea) {
  border-color: #f56c6c;
}

.error-message {
  color: #f56c6c;
  font-size: 0.857rem;
  margin-top: 0.2857rem;
  line-height: 1;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
